import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import uploadIcon from "../../assets/upload-icon.svg";
import "./raise-request.css"; // for legal-modal-btn, legal-changes-btn, legal-filename, etc.
import CustomButton from "../../components/CustomButton/CustomButton";
import closeIcon from '../../assets/close.svg'


interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: { message: string; file: File | null; groupId?: number; groupVersionId?: number; id?: number }) => void;
  meta?: { groupId?: number; groupVersionId?: number; id?: number };
}

const MAX_FILE_SIZE = 10_000_000; // 10MB
const ALLOWED_EXT = ["pdf", "doc", "docx"];

export default function RaiseRequestModal({ show, onClose, onSubmit, meta }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  /** same behaviour as clickOnRef() */
  const handleOpenFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  /** same validation as your original modal */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0] ?? null;

    if (!chosen) {
      setError("File is required");
      setFile(null);
      return;
    }

    if (chosen.size > MAX_FILE_SIZE) {
      setError("File size must be less than 10MB");
      setFile(null);
      return;
    }

    const ext = chosen.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXT.includes(ext)) {
      setError("Only accepts PDF, Word files");
      setFile(null);
      return;
    }

    setError("");
    setFile(chosen);
  };

  const handleRemove = () => {
    setFile(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = () => {
  if (!message.trim()) {
    setError("Message is required");
    return;
  }
  if (!file) {
    setError("File is required");
    return;
  }

  // include meta ids in the payload
  onSubmit({
    message,
    file,
    groupId: meta?.groupId,
    groupVersionId: meta?.groupVersionId,
    id: meta?.id,
  });

  setMessage("");
  setFile(null);
  onClose();
};
const resetRaiseRequestState = () => {
  setMessage("");
  setFile(null);
  setError("");

  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};
const handleClose = () => {
  resetRaiseRequestState();
  onClose(); // original close logic (hide modal)
};


  return (
    <Modal show={show} onHide={onClose} centered>
      {/* <Modal.Header closeButton>
        <Modal.Title>Raise Request</Modal.Title>
      </Modal.Header> */}

      <Modal.Body>
  {/* Center Title + Close Button */}
  <div className="d-flex justify-content-center align-items-center position-relative mb-3">

    {/* Title */}
    <h3 className="mb-0 font-20 weight-500 text-center w-100">
      Raise Request
    </h3>

    {/* Close Button (absolute, right aligned) */}
    <button
      type="button"
      className="border-0 bg-transparent p-0 position-absolute end-0"
      onClick={handleClose}
      style={{ top: 0 }}
    >
      <img src={closeIcon} alt="close" width={18} height={18} />
    </button>

  </div>

        {/* Message */}
        <div className="mb-3">
          <label className="form-label weight-500">Message</label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter your message"
            value={message}
            onChange={(e) => {
              setError("");
              setMessage(e.target.value);
            }}
          />
        </div>

        {/* Upload button — exact UI from your legal modal */}
        <div className="d-flex align-items-center w-100">
  {/* Upload button (left) */}
  <button
    onClick={handleOpenFile}
    className="legal-modal-btn legal-changes-btn d-flex"
    type="button"
    aria-label="Upload file"
  >
    <img width={18} height={18} src={uploadIcon} alt="upload" />
    <p className="mb-0 text-capitalize font-13 weight-500 ms-2">Upload File</p>
  </button>

  <input
    ref={fileInputRef}
    type="file"
    className="d-none"
    accept=".pdf,.doc,.docx"
    onChange={handleFileChange}
  />

  {/* Error message (shows right after upload button) */}
  {error && (
    <p className="mb-0 text-primary-color font-13 weight-400 ms-3">{error}</p>
  )}

  {/* File info + remove icon + Proceed button (shown when file is present and no error) */}
  {file && !error && (
    <>
      {/* filename + remove icon */}
      <div className="d-flex align-items-center ms-3" style={{ maxWidth: "50%" }}>
        <p
          className="mb-0 text-primary-color font-13 weight-400 legal-filename pe-1 me-2 text-truncate"
          style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
          title={file.name}
        >
          {file.name}
        </p>

        <button
          type="button"
          onClick={handleRemove}
          className="btn p-0 ms-2 mf-delete-option"
          aria-label="Remove file"
        />
      </div>

      {/* Proceed button aligned to the right */}
      <div className="ms-3 w-100">
        <CustomButton onClick={handleSubmit}>Proceed</CustomButton>
      </div>
    </>
  )}
</div>

      </Modal.Body>

    </Modal>
  );
}
